import { create } from 'zustand';
import { configApi, workflowApi } from '../services/api';
import type { AppConfig } from '../types';

const DEFAULT_CONFIG: AppConfig = {
  statuses: [
    { id: 'backlog', label: 'Backlog', color: '#94a3b8', order: 0 },
    { id: 'todo', label: 'To Do', color: '#3b82f6', order: 1 },
    { id: 'in-progress', label: 'In Progress', color: '#f59e0b', order: 2 },
    { id: 'review', label: 'In Review', color: '#8b5cf6', order: 3 },
    { id: 'done', label: 'Done', color: '#22c55e', order: 4 },
  ],
  priorities: [
    { id: 'low', label: 'Low', color: '#94a3b8' },
    { id: 'medium', label: 'Medium', color: '#3b82f6' },
    { id: 'high', label: 'High', color: '#f59e0b' },
    { id: 'critical', label: 'Critical', color: '#ef4444' },
  ],
  workItemTypes: [
    { id: 'epic', label: 'Epic', color: '#7c3aed', icon: 'Layers' },
    { id: 'feature', label: 'Feature', color: '#2563eb', icon: 'Package' },
    { id: 'story', label: 'Story', color: '#059669', icon: 'BookOpen' },
    { id: 'task', label: 'Task', color: '#d97706', icon: 'CheckSquare' },
    { id: 'bug',     label: 'Bug',     color: '#ef4444', icon: 'Bug' },
    { id: 'security', label: 'Security', color: '#f97316', icon: 'ShieldAlert' },
    { id: 'lead', label: 'Lead', color: '#0ea5e9', icon: 'Contact' },
    { id: 'opportunity', label: 'Opportunity', color: '#14b8a6', icon: 'Handshake' },
  ],
  healthStatuses: [
    { id: 'on-track', label: 'On Track', color: '#22c55e' },
    { id: 'at-risk', label: 'At Risk', color: '#f59e0b' },
    { id: 'behind', label: 'Behind', color: '#ef4444' },
  ],
  dashboard: {
    widgets: ['projectCards', 'overdueItems', 'healthSummary'],
    quickLinks: [],
  },
};

interface ConfigState {
  config: AppConfig;
  loading: boolean;

  fetchConfig: () => Promise<void>;
  /** Every state defined by every workflow, keyed by id. The real source
   *  of status labels and colours; `config.statuses` is the fallback. */
  workflowStatuses: Record<string, { label: string; color: string }>;
  fetchWorkflowStatuses: () => Promise<void>;
  updateConfig: (data: Partial<AppConfig>) => Promise<void>;
  getStatusColor: (statusId: string) => string;
  getStatusLabel: (statusId: string) => string;
  getPriorityColor: (priorityId: string) => string;
  getWorkItemTypeConfig: (typeId: string) => { label: string; color: string; icon: string };
  getHealthColor: (healthId: string) => string;
}

export const useConfigStore = create<ConfigState>((set, get) => ({
  config: DEFAULT_CONFIG,
  loading: false,

  fetchConfig: async () => {
    set({ loading: true });
    try {
      const config = await configApi.get();
      set({ config: { ...DEFAULT_CONFIG, ...config }, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  workflowStatuses: {},

  /** Load the states of every workflow into one lookup.
   *
   *  A status id that two workflows both define means the same thing in both —
   *  `done` is `done` — so first definition wins rather than the map carrying
   *  duplicates it has no way to choose between.
   */
  fetchWorkflowStatuses: async () => {
    try {
      const workflows = await workflowApi.list({ entityType: 'workitem' });
      const list = Array.isArray(workflows) ? workflows : [];
      const map: Record<string, { label: string; color: string }> = {};
      for (const workflow of list) {
        for (const state of (workflow?.states || [])) {
          if (state?.id && !map[state.id]) {
            map[state.id] = { label: state.label || state.id, color: state.color || '#94a3b8' };
          }
        }
      }
      set({ workflowStatuses: map });
    } catch {
      // Leave the fallback in place. A status chip is not worth a failed boot.
    }
  },

  updateConfig: async (data) => {
    await configApi.update(data);
    set((s) => ({ config: { ...s.config, ...data } }));
  },

  // Workflows first, `config.statuses` second. The board has taken its columns
  // from the resolved workflow since the engine landed; these lookups had not
  // moved, so anything outside the legacy seven — 37 of 44 states — came back
  // grey with its raw slug showing.
  getStatusColor: (statusId) => {
    const fromWorkflow = get().workflowStatuses[statusId];
    if (fromWorkflow) return fromWorkflow.color;
    return get().config.statuses.find((s) => s.id === statusId)?.color || '#94a3b8';
  },

  getStatusLabel: (statusId) => {
    const fromWorkflow = get().workflowStatuses[statusId];
    if (fromWorkflow) return fromWorkflow.label;
    return get().config.statuses.find((s) => s.id === statusId)?.label || statusId;
  },

  getPriorityColor: (priorityId) => {
    const p = get().config.priorities.find((p) => p.id === priorityId);
    return p?.color || '#94a3b8';
  },

  getWorkItemTypeConfig: (typeId) => {
    const t = get().config.workItemTypes.find((t) => t.id === typeId);
    return t || { label: typeId, color: '#94a3b8', icon: 'Circle' };
  },

  getHealthColor: (healthId) => {
    const h = get().config.healthStatuses.find((h) => h.id === healthId);
    return h?.color || '#94a3b8';
  },
}));
